import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  promoCodes: {
    data: [],
    meta: {
      total: 0,
    },
  },
  singlePromoCode: null,
  promoCodeUsers: {
    data: [],
    meta: {
      total: 0,
    },
  },
  promoCodeOrders: {
    data: [],
    meta: {
      total: 0,
    },
  },
};

export const fetchPromoCodes = createAsyncThunk(
  "promoCode/fetchAll",
  async ({ enqueueSnackbar, limit = 20, page = 0, ...params }, thunkApi) => {
    try {
      const response = await axiosInstance.get("admin/promo-codes", {
        params: {
          per_page: limit,
          page: page + 1,
          ...params,
        },
      });
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const createPromoCode = createAsyncThunk(
  "promoCode/create",
  async ({ data, enqueueSnackbar, navigate, callback }, thunkApi) => {
    try {
      const response = await axiosInstance.post("admin/promo-codes", data);
      enqueueSnackbar("Promo code created successfully");
      if (callback) callback();
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updatePromoCode = createAsyncThunk(
  "promoCode/update",
  async ({ id, data, enqueueSnackbar, callback }, thunkApi) => {
    try {
      const response = await axiosInstance.patch(`admin/promo-codes/${id}`, data);
      enqueueSnackbar("Promo code updated successfully");
      if (callback) callback();
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deletePromoCode = createAsyncThunk(
  "promoCode/delete",
  async ({ id, enqueueSnackbar }, thunkApi) => {
    try {
      await axiosInstance.delete(`admin/promo-codes/${id}`);
      enqueueSnackbar("Promo code deleted successfully");
      return id;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchPromoCodeUsers = createAsyncThunk(
  "promoCode/fetchUsers",
  async ({ id, enqueueSnackbar, limit = 20, page = 0 }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`admin/promo-codes/${id}/users`, {
        params: {
          per_page: limit,
          page: page + 1,
        },
      });
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchPromoCodeOrders = createAsyncThunk(
  "promoCode/fetchOrders",
  async ({ id, enqueueSnackbar, limit = 20, page = 0 }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`admin/promo-codes/${id}/orders`, {
        params: {
          per_page: limit,
          page: page + 1,
        },
      });
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const promoCodeSlice = createSlice({
  name: "promoCode",
  initialState,
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchPromoCodes.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.promoCodes = action.payload;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.fetchLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to fetch promo codes", {
            variant: "error",
          });
        }
      })
      // Create
      .addCase(createPromoCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPromoCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.isLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to create promo code", {
            variant: "error",
          });
        }
      })
      // Update
      .addCase(updatePromoCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePromoCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updatePromoCode.rejected, (state, action) => {
        state.isLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to update promo code", {
            variant: "error",
          });
        }
      })
      // Delete
      .addCase(deletePromoCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.promoCodes.data = state.promoCodes.data.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.isLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to delete promo code", {
            variant: "error",
          });
        }
      })
      // Fetch Users
      .addCase(fetchPromoCodeUsers.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchPromoCodeUsers.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.promoCodeUsers = action.payload;
      })
      .addCase(fetchPromoCodeUsers.rejected, (state, action) => {
        state.fetchLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to fetch promo code users", {
            variant: "error",
          });
        }
      })
      // Fetch Orders
      .addCase(fetchPromoCodeOrders.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchPromoCodeOrders.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.promoCodeOrders = action.payload;
      })
      .addCase(fetchPromoCodeOrders.rejected, (state, action) => {
        state.fetchLoading = false;
        const { enqueueSnackbar, error } = action.payload || {};
        if (enqueueSnackbar) {
          enqueueSnackbar(error?.message || "Failed to fetch promo code orders", {
            variant: "error",
          });
        }
      });
  },
});

export default promoCodeSlice.reducer;
